import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import * as RadixDropdown from "@radix-ui/react-dropdown-menu";

import { fadeInAndSlideUp } from "../../../styles/animations";

type DropdownMenuProps = {
  trigger: ReactNode; // the element that will trigger the dropdown, e.g. a button.
  // the component must spread props and forward refs
  children: ReactNode; // the content of the dropdown menu, e.g. a list of items.

  // Trigger Props
  modal?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  // Content Props
  align?: "start" | "center" | "end";
  width?: string | number;

  // Whether to show a chevron icon in the trigger
  chevron?: boolean;
};

function DropdownMenu({
  trigger,
  children,
  // Trigger Props
  modal = true,
  open,
  onOpenChange,
  // Content Props
  align = "start",
  width,
  chevron = true,
}: DropdownMenuProps) {
  return (
    <Wrapper onOpenChange={onOpenChange} open={open} modal={modal}>
      <TriggerWrapper asChild aria-label="dropdown-menu" chevron={chevron}>
        {trigger}
      </TriggerWrapper>
      <Content align={align} sideOffset={4} width={width}>
        {children}
      </Content>
    </Wrapper>
  );
}

export default DropdownMenu;

const Wrapper = styled(RadixDropdown.Root)({
  zIndex: 1000,
});

const TriggerWrapper = styled(RadixDropdown.Trigger)<{ chevron: boolean }>(({ chevron }) => ({
  "> svg:last-of-type": {
    display: chevron ? "block" : "none",
  },
}));

const Content = styled(RadixDropdown.Content)<{ width?: number | string }>(({ width, theme }) => ({
  display: "flex",
  position: "relative",
  flexDirection: "column",
  gap: 4,
  alignItems: "flex-start",
  backgroundColor: "white",
  borderRadius: 4,
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.07)",
  padding: 4,
  width: width ?? 280,
  boxSizing: "border-box",
  fontFamily: theme.fontFamily,
  animation: `${fadeInAndSlideUp} 0.3s ease-out`,
  zIndex: 1000,
}));
